import React, { FC, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import AuthorizationService from '../../../../authorization/AuthorizationService';
import VendorImg from '../../../../images/member-grid-placeholder.jpg'; // Using same placeholder for now
import { DeleteConfirmationModal } from '../../../../components/DeleteConfirmationModal';
import { deleteOtherVendor } from '../../../../api/vendors/otherVendors';
import { fetchOtherVendorsList } from '../../../../actions/vendors/otherVendors';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Grid,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { toast } from 'react-toastify';

const useStyles = makeStyles(() => ({
  img: {
    objectFit: 'contain',
  },
}));

export const VendorGridCard: FC<{
  vendor: {
    vendor_logo: null | string;
    id: number | any;
    name: null | string;
    description: string;
    vendor_url: null | string;
  };
  pageNumber: number;
  pageSize: number;
}> = ({ vendor, pageNumber, pageSize }) => {
  const {
    id,
    name,
    description,
    vendor_logo,
    vendor_url,
  } = vendor ?? {};

  const tempFormattedLogo =
    vendor_logo?.substring(0, 1) === '/' ? vendor_logo : `/${vendor_logo}`; // TODO this forces old style to still work
  const [nav, setNav] = useState('none');
  const [imageBroken, setImageBroken] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const cardImg = !vendor_logo || imageBroken ? VendorImg : tempFormattedLogo;

  const classes = useStyles();
  const dispatch = useDispatch();

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteOtherVendor(id);
      setIsDeleteModalOpen(false);
      toast.success(`Vendor "${name}" has been deleted successfully`);
      
      // Refresh the vendors list via Redux action
      dispatch(fetchOtherVendorsList() as any);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error('Failed to delete vendor. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };
  
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      {nav === 'edit' && (
        <Navigate
          to={`/vendors/other-vendors/edit/${id}/`}
          state={{
            detail: vendor,
            navigateFrom: 'LIST',
            pageType: 'EDIT',
            isFromVendorTab: true,
            path : '/vendors/other-vendors' 
          }}
        />
      )}
      <Card className="card-member-box">
        <div className="card-media-img">
          <CardMedia
            className={classes.img}
            onError={() => {
              setImageBroken(true);
            }}
            component="img"
            height="140"
            image={cardImg}
            alt={name || ''}
            title={name || ''}
            onClick={(e: React.MouseEvent<HTMLImageElement>) => {
              if (vendor_url) {
                e.stopPropagation();
                window.open(vendor_url, '_blank', 'noopener,noreferrer');
              }
            }}
            style={vendor_url ? { cursor: 'pointer' } : undefined}
          />
          <CardActions className="members-actions">
            {AuthorizationService.isAuthorizedHTML('editvendorbutton') && (
              <Tooltip title="Edit">
                <Button type="button" onClick={() => setNav('edit')}>
                  <i className="fas fa-pen" />
                </Button>
              </Tooltip>
            )}
            {AuthorizationService.isAuthorizedHTML('deletevendorbutton') && (
              <Tooltip title="Delete">
                <Button 
                  type="button"
                  onClick={handleDeleteClick}
                  style={{ color: '#dc3545' }}
                >
                  <i className="fas fa-trash" />
                </Button>
              </Tooltip>
            )}
          </CardActions>
        </div>
        <CardContent>
          <Typography
            gutterBottom
            variant="h5"
            component="h2"
            className="member-title"
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            className="member-place"
          >
            {description}
          </Typography>
        </CardContent>
      </Card>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        title="Delete Vendor"
        itemName={name || undefined}
        itemType="vendor"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />
    </Grid>
  );
};

export default VendorGridCard;
